/**
 * The M1 slot surfaces (surfaces.md §2.2, §3): one model, pure, for every
 * closed shape she types. A surface is a row of slots; a key lands in one
 * slot, or moves between them, or commits. Nothing auto-advances when a slot
 * fills: the separator key is the only way out of a slot, and it is the same
 * key she writes on paper (`/`, `:`, `;`, `^`, Space in a mixed number).
 *
 * Every leaf is a string exactly as left in the slot: `2,5` keeps its comma,
 * `2,,8` keeps both, `1 200` keeps its space, a later minus stays and is
 * judged. The judge normalises; this never does. The two on-screen
 * conversions are the surface's own and the trace holds the keys: `inf` → `∞`
 * in an interval bound, and `*` → `·` in an expression.
 */
import { type KeyLike, digitOfCode, isBrowserChord, isInert, isPrintable, readChar } from "./keys";

export type SlotKind = "num" | "expr" | "bound" | "open" | "close" | "unit";

export interface SlotSpec {
  id: string;
  kind: SlotKind;
}

interface Sep {
  chars: string[];
  from: string[];
}

export interface SlotSurface {
  id: string;
  mode: string;
  slots: SlotSpec[];
  seps: Sep[];
}

export interface SlotState {
  values: Record<string, string>;
  active: number;
  caret: number;
  /** qty only: the unit id she picked from the question's list, "" if none. */
  unit: string;
}

/** One trace entry without its time: [code, key, slot, op, x?]. */
export type Entry = [string, string, string, string] | [string, string, string, string, string];

export type Action = "commit" | "skip" | null;

export interface Step {
  state: SlotState;
  entry: Entry | null;
  action: Action;
  /** true when the browser must be left to do its own thing (Tab off the last slot, Ctrl+C). */
  native?: boolean;
}

const DIGITS = "0123456789";
const SUPERS = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const NUM_CHARS = DIGITS + ",.-−+ ";
const EXPR_CHARS = NUM_CHARS + "·*^()/" + SUPERS;

/** The surfaces M1 draws, from `{id, mode}` as the question declares them. */
export function slotSurface(id: string, mode: string): SlotSurface | null {
  const s = (slots: [string, SlotKind][], seps: Sep[] = []): SlotSurface => ({
    id,
    mode,
    slots: slots.map(([sid, kind]) => ({ id: sid, kind })),
    seps,
  });
  switch (id) {
    case "num":
      return s([["v", mode === "expr" ? "expr" : "num"]]);
    case "frac":
      return mode === "mixed"
        ? s(
            [
              ["w", "num"],
              ["n", "num"],
              ["d", "num"],
            ],
            [
              { chars: [" "], from: ["w"] },
              { chars: ["/"], from: ["n"] },
            ],
          )
        : s(
            [
              ["n", "num"],
              ["d", "num"],
            ],
            [{ chars: ["/"], from: ["n"] }],
          );
    case "ratio": {
      const n = Math.max(2, Number.parseInt(mode, 10) || 2);
      const parts = Array.from({ length: n }, (_, i) => [`p${i}`, "num"] as [string, SlotKind]);
      const from = parts.slice(0, -1).map(([p]) => p);
      return s(parts, [{ chars: n > 2 ? [":", ";"] : [":"], from }]);
    }
    case "stdform":
      return s(
        [
          ["m", "num"],
          ["e", "num"],
        ],
        [{ chars: ["^", "e", "E"], from: ["m"] }],
      );
    case "point":
      return s(
        [
          ["x", "num"],
          ["y", "num"],
        ],
        [{ chars: [";"], from: ["x"] }],
      );
    case "interval":
      return s(
        [
          ["lb", "open"],
          ["lo", "bound"],
          ["hi", "bound"],
          ["rb", "close"],
        ],
        [{ chars: [";"], from: ["lo"] }],
      );
    case "qty":
      return s([
        ["v", "num"],
        ["unit", "unit"],
      ]);
    default:
      return null;
  }
}

export function initial(surface: SlotSurface): SlotState {
  const values: Record<string, string> = {};
  for (const sl of surface.slots) values[sl.id] = "";
  return { values, active: 0, caret: 0, unit: "" };
}

/** `input` for answer.submitted (surfaces.md §2.2): every leaf a string, an
 *  empty slot `""`, never absent. */
export function buildInput(surface: SlotSurface, st: SlotState): Record<string, unknown> {
  const v = st.values;
  switch (surface.id) {
    case "ratio":
      return { parts: surface.slots.map((s) => v[s.id]) };
    case "qty":
      return { v: v.v, unit: st.unit, unit_typed: v.unit };
    default: {
      const out: Record<string, string> = {};
      for (const s of surface.slots) out[s.id] = v[s.id];
      return out;
    }
  }
}

/** Non-empty means at least one slot holds something she typed. */
export function isEmpty(st: SlotState): boolean {
  return Object.values(st.values).every((x) => x === "") && st.unit === "";
}

function accepts(kind: SlotKind, ch: string): boolean {
  switch (kind) {
    case "num":
      return NUM_CHARS.includes(ch);
    case "expr":
      return EXPR_CHARS.includes(ch) || /^[A-Za-z]$/.test(ch);
    case "bound":
      return NUM_CHARS.includes(ch) || "inf∞".includes(ch);
    case "open":
      return ch === "(" || ch === "[";
    case "close":
      return ch === ")" || ch === "]";
    case "unit":
      return ch !== " ";
  }
}

function sepFor(surface: SlotSurface, slotId: string, ch: string): boolean {
  return surface.seps.some((s) => s.chars.includes(ch) && s.from.includes(slotId));
}

/** The unit options whose label or id starts with what she typed. */
export function unitMatches(options: { id: string; label: string }[], typed: string): { id: string; label: string }[] {
  const t = typed.trim().toLowerCase();
  if (!t) return [];
  return options.filter((o) => o.label.toLowerCase().startsWith(t) || o.id.toLowerCase().startsWith(t));
}

function insert(st: SlotState, slotId: string, text: string, caret: number): SlotState {
  const cur = st.values[slotId];
  const next = cur.slice(0, caret) + text + cur.slice(caret);
  return { ...st, values: { ...st.values, [slotId]: next }, caret: caret + text.length };
}

/** `inf` typed into a bound becomes ∞ on screen; the trace keeps i, n, f. */
function infinity(st: SlotState, slotId: string): SlotState {
  const cur = st.values[slotId];
  if (!/inf$/i.test(cur.slice(0, st.caret))) return st;
  const before = cur.slice(0, st.caret - 3);
  return { ...st, values: { ...st.values, [slotId]: before + "∞" + cur.slice(st.caret) }, caret: before.length + 1 };
}

export interface Ctx {
  /** Enter commits („Tikrinti", or „Toliau" in a test). */
  test: boolean;
  /** qty: the question's unit list. */
  units?: { id: string; label: string }[];
}

/**
 * One keydown on a slot surface. The caller attaches the time and writes the
 * trace; the caller also calls `preventDefault` unless `native` is set.
 */
export function applyKey(surface: SlotSurface, st: SlotState, k: KeyLike, ctx: Ctx): Step {
  const slot = surface.slots[st.active];
  const sid = slot.id;
  const at = (x?: string): Entry => (x === undefined ? [k.code, k.key, sid, ""] : [k.code, k.key, sid, "", x]);
  const e = (op: string, x?: string): Entry => {
    const en = at(x);
    en[3] = op;
    return en;
  };

  if (k.key === "Enter") {
    if (k.repeat) return { state: st, entry: e("repeat"), action: null };
    if ((k.ctrlKey || k.metaKey) && ctx.test) return { state: st, entry: e("skip"), action: "skip" };
    // A unit typed and not yet picked: Enter picks the first match (§2.2 qty).
    if (slot.kind === "unit" && ctx.units) {
      const m = unitMatches(ctx.units, st.values.unit);
      if (m.length && st.unit !== m[0].id) {
        return { state: { ...st, unit: m[0].id }, entry: e("pick", m[0].id), action: null };
      }
    }
    if (isEmpty(st)) return { state: st, entry: e("enter_empty"), action: null };
    return { state: st, entry: e("enter"), action: "commit" };
  }
  if (isInert(k)) return { state: st, entry: null, action: null, native: true };
  if (isBrowserChord(k)) return { state: st, entry: null, action: null, native: true };

  const last = surface.slots.length - 1;
  const cur = st.values[sid];
  switch (k.key) {
    case "Tab":
      if (k.shiftKey) {
        if (st.active === 0) return { state: st, entry: null, action: null, native: true };
        const p = st.active - 1;
        return { state: { ...st, active: p, caret: st.values[surface.slots[p].id].length }, entry: e("slot_prev"), action: null };
      }
      if (st.active === last) return { state: st, entry: null, action: null, native: true };
      return { state: { ...st, active: st.active + 1, caret: st.values[surface.slots[st.active + 1].id].length }, entry: e("slot_next"), action: null };
    case "ArrowLeft":
      if (st.caret > 0) return { state: { ...st, caret: st.caret - 1 }, entry: e("caret"), action: null };
      if (st.active > 0) {
        const p = st.active - 1;
        return { state: { ...st, active: p, caret: st.values[surface.slots[p].id].length }, entry: e("caret"), action: null };
      }
      return { state: st, entry: e("caret"), action: null };
    case "ArrowRight":
      if (st.caret < cur.length) return { state: { ...st, caret: st.caret + 1 }, entry: e("caret"), action: null };
      if (st.active < last) return { state: { ...st, active: st.active + 1, caret: 0 }, entry: e("caret"), action: null };
      return { state: st, entry: e("caret"), action: null };
    case "Home":
      return { state: { ...st, active: 0, caret: 0 }, entry: e("caret"), action: null };
    case "End":
      return { state: { ...st, active: last, caret: st.values[surface.slots[last].id].length }, entry: e("caret"), action: null };
    case "Backspace": {
      if (st.caret === 0) {
        if (st.active === 0) return { state: st, entry: e("bksp", "start"), action: null };
        const p = st.active - 1;
        return { state: { ...st, active: p, caret: st.values[surface.slots[p].id].length }, entry: e("slot_prev"), action: null };
      }
      const next = cur.slice(0, st.caret - 1) + cur.slice(st.caret);
      const unit = slot.kind === "unit" ? "" : st.unit; // editing the typed unit un-picks it
      return { state: { ...st, values: { ...st.values, [sid]: next }, caret: st.caret - 1, unit }, entry: e("bksp", cur[st.caret - 1]), action: null };
    }
    case "Delete": {
      if (st.caret >= cur.length) return { state: st, entry: e("del", "end"), action: null };
      const next = cur.slice(0, st.caret) + cur.slice(st.caret + 1);
      const unit = slot.kind === "unit" ? "" : st.unit;
      return { state: { ...st, values: { ...st.values, [sid]: next }, unit }, entry: e("del", cur[st.caret]), action: null };
    }
  }

  if (!isPrintable(k) && k.key !== "Dead" && digitOfCode(k.code) === null) {
    // Escape, arrows up/down, PageUp…: not an answer key; logged as refused.
    return { state: st, entry: e("refuse", "not_in_slot"), action: null };
  }

  // Interval brackets typed from inside a bound set the bracket beside it.
  const bracketHere = (ch: string) =>
    (sid === "lo" && (ch === "(" || ch === "[")) || (sid === "hi" && (ch === ")" || ch === "]"));
  // A mixed number's whole slot, left unused: `/` there moves what she typed
  // into the numerator (`7/4` is `{w: "", n: "7", d: "4"}`, §2.2).
  const wholeToFraction = (ch: string) => surface.id === "frac" && surface.mode === "mixed" && sid === "w" && ch === "/";
  const meaningful = (ch: string) =>
    sepFor(surface, sid, ch) || bracketHere(ch) || wholeToFraction(ch) || accepts(slot.kind, ch);
  const r = readChar(k, meaningful);

  if (r.kind === "refuse") return { state: st, entry: e("refuse", r.why), action: null };
  if (r.kind === "code") {
    if (slot.kind === "open" || slot.kind === "close") return { state: st, entry: e("refuse", "not_in_slot"), action: null };
    const n = insert(st, sid, r.ch, st.caret);
    return { state: slot.kind === "unit" ? { ...n, unit: "" } : n, entry: e("ins_code", r.ch), action: null };
  }
  const ch = r.ch;
  if (sepFor(surface, sid, ch)) {
    const n = st.active + 1;
    return { state: { ...st, active: n, caret: st.values[surface.slots[n].id].length }, entry: e("slot_next", ch), action: null };
  }
  if (wholeToFraction(ch)) {
    const moved = { ...st.values, w: "", n: st.values.n || cur };
    const inNumerator = st.values.n === "";
    return {
      state: { ...st, values: inNumerator ? moved : st.values, active: 2, caret: st.values.d.length },
      entry: e("struct", inNumerator ? "w_to_n" : "slot_next"),
      action: null,
    };
  }
  if (bracketHere(ch)) {
    const target = sid === "lo" ? "lb" : "rb";
    const values = { ...st.values, [target]: ch };
    if (sid === "hi") {
      return { state: { ...st, values, active: 3, caret: 1 }, entry: [k.code, k.key, target, "ins"], action: null };
    }
    return { state: { ...st, values }, entry: [k.code, k.key, target, "ins"], action: null };
  }
  if (slot.kind === "open" || slot.kind === "close") {
    // One character; typing the bracket replaces it, and an open bracket
    // carries her on into the lower bound, which is where the next key goes.
    const values = { ...st.values, [sid]: ch };
    if (slot.kind === "open") return { state: { ...st, values, active: 1, caret: st.values.lo.length }, entry: e("ins"), action: null };
    return { state: { ...st, values, caret: 1 }, entry: e("ins"), action: null };
  }
  const shown = slot.kind === "expr" && ch === "*" ? "·" : ch;
  let n = insert(st, sid, shown, st.caret);
  if (slot.kind === "bound") n = infinity(n, sid);
  if (slot.kind === "unit") n = { ...n, unit: "" };
  return { state: n, entry: e("ins", shown !== ch ? shown : undefined), action: null };
}

/** A paste: kept whole, whatever it holds (§3.3). One `paste` entry with its text. */
export function applyPaste(surface: SlotSurface, st: SlotState, text: string): Step {
  const sid = surface.slots[st.active].id;
  const one = text.replace(/[\r\n]+/g, " ");
  return { state: insert(st, sid, one, st.caret), entry: ["", "", sid, "paste", one], action: null };
}

/** A click into a slot (her mouse, §3.5: code `Mouse0`, key the target). */
export function applyClick(surface: SlotSurface, st: SlotState, index: number, caret: number): Step {
  const sid = surface.slots[index].id;
  const c = Math.max(0, Math.min(caret, st.values[sid].length));
  return { state: { ...st, active: index, caret: c }, entry: ["Mouse0", `slot:${sid}`, sid, "focus"], action: null };
}

/** Picking a unit from the list with the mouse. */
export function applyUnitPick(st: SlotState, id: string): Step {
  return { state: { ...st, unit: id }, entry: ["Mouse0", `unit:${id}`, "unit", "pick", id], action: null };
}
